<template>
  <div
    class="
      layout/flex:column
      layout/grid:gap:page:2
    "
    >
    <ExhibitionHistoryForm
      :begin-date="params.begin_date"
      :end-date="params.end_date"
      :location="params.location"
      :mde-type="params.mde_type"
      :q="params.q"
      :sort-date="params.sort_date"
      @form-submit="onFormSubmit"
      @search-input="onSearchInput"
      @search-reset="onSearchReset"
      @update-datespan="onUpdateDatespan"
      @update-location="onUpdateLocation"
      @update-mde-type="onUpdateMdeType"
      @update-sort-date="onUpdateSortDate"
    />
    <div
      v-if="params.constituent_id > 0"
      class="
        layout/align:start
        layout/flex:column
        layout/wrapper
      "
      >
      <FilteredPaginationFilterPill
        :title="constituentName"
        aria-label="Remove constituent pill"
        data-gtm="exhibition history / remove constituent pill"
        @removePill="onRemovePill"
      />
    </div>
    <div
      class="
        $typography/size:medium
        layout/height:cap
        layout/width:100%
        layout/wrapper
        "
      >
      <FadeTransition>
        <FilteredPaginationFilterCount
          v-if="!showSpinner"
          :filtered-total="filteredTotal"
          :label-online="i18n.t('filter-form__label__online')"
          :message-filtered-count="i18n.t('filter-form__filter_count__filtered-count', { FilteredCount: filteredTotal.toLocaleString() })"
          :message-filtered-no-count="i18n.t('filter-form__filter_count__filtered-no-count')"
          :message-search-term-no-results="i18n.t('filter-form__filter_count__no-results-for-search-term', { SearchVal: params.q })"
          :message-total-count-online="i18n.t('filter-form__filter_count__total-count-online', { TotalCount: total.toLocaleString(), Resource: tResource })"
          :message-total-count="i18n.t('filter-form__filter_count__total-count', { TotalCount: total.toLocaleString(), Resource: tResource })"
          :search-val="params.q"
          :total="total"
          data-gtm="exhibitions history form / reset form"
          href="/calendar/exhibitions/history/"
          @resetForm="onResetForm"
        />
        <Spinner v-if="showSpinner" />
      </FadeTransition>
    </div>
  </div>
</template>

<script>
  /**
   * FormWrapper manages the submission of the ExhibitionHistoryForm.
   *
   * The responsibility of this component is to manage state for:
   *  - load filtered results
   *  - load paginated results
   *  - updating browser history
   */
  import { MoMABalanceText } from 'sol';
  import i18n from 'Utils/i18n.js';

  // Form
  import ExhibitionHistoryForm from "./exhibition-history-form.vue";
  import FadeTransition from 'Components/shared/fade-transition.vue';
  import FilteredPaginationFilterCount from 'Components/filtered-pagination/filter-count.vue';
  import FilteredPaginationFilterPill from 'Components/filtered-pagination/filter-pill.vue';
  import Spinner from 'Components/shared/spinner.vue';
  import { options } from './options';

  // Filter/pagination
  import FilteredPaginationMixin from 'Components/filtered-pagination/filtered-pagination-mixin.js';
  import HistoryStackableMixin from 'Components/filtered-pagination/history-stackable-mixin.js';

  export default {
    components: {
      ExhibitionHistoryForm,
      FadeTransition,
      FilteredPaginationFilterCount,
      FilteredPaginationFilterPill,
      Spinner,
    },
    mixins: [
      FilteredPaginationMixin,
      HistoryStackableMixin
    ],
    props: {
      // Form
      dataConstituentName: { type: String, required: true },
      dataFilteredTotal: { type: Number, required: true },
      dataTotal: { type: Number, required: true },
      paramBeginDate: { type: String, required: true },
      paramConstituentId: { type: Number, required: true },
      paramEndDate: { type: String, required: true },
      paramLocation: { type: String, required: true },
      paramMdeType: { type: String, required: true },
      paramSearchTerm: { type: String, required: true },
      paramSortOrder: { type: String, required: true },
    },

    data() {
      return {
        i18n: new i18n(),
        balanceText: new MoMABalanceText(),
        tResource: 'exhibitions',

        // Form
        params: {
          begin_date: this.paramBeginDate,
          constituent_id: this.paramConstituentId,
          end_date: this.paramEndDate,
          location: this.paramLocation,
          mde_type: this.paramMdeType,
          q: this.paramSearchTerm,
          sort_date: this.paramSortOrder,
        },
        constituentName: this.dataConstituentName,
        filteredTotal: this.dataFilteredTotal,
        total: this.dataTotal,
      };
    },

    mounted() {
      // Handles navigation with back button.
      window.addEventListener("popstate", this.onHandlePopState, false);

      if (this.lastPage > 1) {
        const url = new URL(this.dataFetchBaseURL, window.location);
        const urlSearchParams = this.setUrlSearchParams(this.dataFetchBaseURL, this.params).searchParams;
        this.addPagination(url, urlSearchParams);
      }

      if (this.$el) {
        this.balanceText.balance(this.$el.getElementsByClassName('balance-text'));
      }
    },

    beforeDestroy() {
      window.removeEventListener("popstate", this.onHandlePopState);
    },

    methods: {
      /**
       * Callback action emitted from Form child component after form submission.
       */
      onFormSubmit() {
        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from browser when history stack navigation is used (Back/Forwards buttons).
       */
      onHandlePopState() {
        const poppedUrl = new URL(window.location);
        const searchParams = this.extractSearchParamsFromUrl(poppedUrl.searchParams);
        this.params = {...this.params, ...searchParams};

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url);
      },

      /**
       * Callback action emitted from Form child component to reset reactive data.
       */
      onResetForm() {
        this.params = Object.assign(this.params, options.defaultParams);

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from ExhibitionPill child component to reset reactive `constituent_id` data.
       */
      onRemovePill() {
        this.params.constituent_id = -1;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from Form child component to update reactive `q` data.
       *
       * @param {string} val
       */
      onSearchInput(val) {
        this.params.q = val;
      },

      /**
       * Callback action emitted from Form child component to clear reactive `q` data.
       */
      onSearchReset() {
        this.params.q = '';

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from child component to update reactive `begin_date` and `end_date` data.
       *
       * @param {string} val
       */
       onUpdateDatespan(vals) {
        this.params.begin_date = vals.beginDate;
        this.params.end_date = vals.endDate;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from child component to update reactive `location` data.
       *
       * @param {string} val
       */
       onUpdateLocation(val) {
        this.params.location = val;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from child component to update reactive `mde_type` data.
       *
       * @param {string} val
       */
       onUpdateMdeType(val) {
        this.params.mde_type = val;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from child component to update reactive `sort_date` data.
       *
       * @param {string} val
       */
       onUpdateSortDate(val) {
        this.params.sort_date = val;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Extracts a predefined set of search params from a URL.
       *
       * @param {URLSearchParam} urlSearchParam
       * @returns {Object} An object composed of values extracted from search parameters or default.
       */
      extractSearchParamsFromUrl(urlSearchParam) {
        return {
          begin_date: urlSearchParam.get('begin_date') || options.defaultParams.begin_date,
          constituent_id: parseInt(urlSearchParam.get('constituent_id')) || options.defaultParams.constituent_id,
          end_date: urlSearchParam.get('end_date') || options.defaultParams.end_date,
          location: urlSearchParam.get('location') || options.defaultParams.location,
          mde_type: urlSearchParam.get('mde_type') || options.defaultParams.mde_type,
          q: urlSearchParam.get('q') || options.defaultParams.q,
          sort_date: urlSearchParam.get('sort_date') || options.defaultParams.sort_date,
        };
      },

      /**
       * Wrapper for `fetchData()` to handle shared rendering callback logic.
       *
       * @param {string} url
       */
      async fetchAndDisplayResults(url) {
        this.displaySpinner();
        this.hidePaginationContainer();

        this.fetchData(url)
          .then(res => this.populatePaginationResults(res))
          .then(this.hideSpinner)
          .then(this.displayPaginationContainer)
          .then(() => {
            if (this.lastPage > 1) {
              const urlSearchParams = this.setUrlSearchParams(this.dataFetchBaseURL, this.params).searchParams;
              this.addPagination(url, urlSearchParams);
            } else {
              this.removePagination();
            }
          });
      },
    }
  };
</script>
